export type QueryValue = string | readonly string[] | undefined

/** Projects a contract query object to wire parameters; a key the contract omits cannot be sent. */
export type QueryProjection<Q> = {
  [K in keyof Q]-?: (value: NonNullable<Q[K]>) => QueryValue
}

export function projectQuery<Q extends object>(
  query: Q,
  projection: QueryProjection<Q>,
): Record<string, QueryValue> {
  const out: Record<string, QueryValue> = {}
  for (const key of Object.keys(projection) as (keyof Q & string)[]) {
    const value = query[key]
    if (value === undefined || value === null) continue
    const project = projection[key] as (input: NonNullable<Q[keyof Q & string]>) => QueryValue
    out[key] = project(value as NonNullable<Q[keyof Q & string]>)
  }
  return out
}

function encodePair(key: string, value: string): string {
  return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
}

function pairs(params: Readonly<Record<string, QueryValue>>): string[] {
  const out: string[] = []
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue
    if (typeof value === 'string') out.push(encodePair(key, value))
    else for (const item of value) out.push(encodePair(key, item))
  }
  return out
}

/**
 * A cache key that is injective over its input: every component is percent-encoded
 * before a delimiter is added, so a delimiter in the output is always a delimiter, and
 * the sort is by code point rather than by a host's collation.
 */
export function queryFingerprint(params: Readonly<Record<string, QueryValue>>): string {
  return pairs(params)
    .sort((a, b) => (a < b ? -1 : a > b ? 1 : 0))
    .join('&')
}

export function buildUrl(
  baseUrl: string,
  path: string,
  params: Readonly<Record<string, QueryValue>> = {},
): string {
  const search = pairs(params).join('&')
  const base = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl
  return search === '' ? `${base}${path}` : `${base}${path}?${search}`
}

/** Key-sorted JSON, so two spellings of one payload fingerprint the same. */
export function canonicalJson(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null'
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, item]) => item !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([key, item]) => `${JSON.stringify(key)}:${canonicalJson(item)}`)
  return `{${entries.join(',')}}`
}
